import React from "react";
import { MenuItem, Paper, TextField, Typography } from "@mui/material";

import SalesFieldRenderer from "../SalesFieldRenderer/SalesFieldRenderer";
import {
  SALES_COLUMN_COMMON_FIELDS,
  SALES_COLUMN_FIELDS_BY_TYPE,
  SALES_COLUMN_TYPE_OPTIONS,
} from "../../../../../constants";

// Build a column object seeded with the defaults for a given column type.
export const buildDefaultColumn = (type = "richText") => {
  const col = { type };
  SALES_COLUMN_COMMON_FIELDS.forEach((f) => {
    if (f.default !== undefined) col[f.key] = f.default;
  });
  (SALES_COLUMN_FIELDS_BY_TYPE[type] || []).forEach((f) => {
    if (f.default !== undefined) col[f.key] = f.default;
  });
  return col;
};

/**
 * Editor for one column of a `twoColumn` section. Renders a column-type
 * dropdown, the common fields (weight, mobileOrder), and the fields specific to
 * the selected type. Reads/writes a nested column object
 * `{ type, weight, mobileOrder, ...typeFields }`.
 */
const ColumnEditor = ({ label, value, page, disabled, onChange }) => {
  const column = value || buildDefaultColumn();
  const type = column.type || "richText";
  const typeFields = SALES_COLUMN_FIELDS_BY_TYPE[type] || [];

  const setField = (key, v) => onChange({ ...column, [key]: v });

  // Switching type: keep common fields, reset to the new type's defaults, and
  // drop fields that belong to the previous type.
  const handleTypeChange = (nextType) => {
    const next = { type: nextType };
    SALES_COLUMN_COMMON_FIELDS.forEach((f) => {
      if (column[f.key] !== undefined) next[f.key] = column[f.key];
    });
    (SALES_COLUMN_FIELDS_BY_TYPE[nextType] || []).forEach((f) => {
      if (f.default !== undefined && next[f.key] === undefined) {
        next[f.key] = f.default;
      }
    });
    onChange(next);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography sx={headingStyles}>{label}</Typography>

      <Typography sx={labelStyles}>Column Type</Typography>
      <TextField
        select
        fullWidth
        size="small"
        value={type}
        disabled={disabled}
        onChange={(e) => handleTypeChange(e.target.value)}
        sx={{ mb: 2.5 }}
      >
        {SALES_COLUMN_TYPE_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>

      {SALES_COLUMN_COMMON_FIELDS.map((field) => (
        <SalesFieldRenderer
          key={field.key}
          field={field}
          value={column[field.key]}
          page={page}
          disabled={disabled}
          onChange={(v) => setField(field.key, v)}
        />
      ))}

      {typeFields.map((field) => (
        <SalesFieldRenderer
          key={field.key}
          field={field}
          value={column[field.key]}
          page={page}
          disabled={disabled}
          onChange={(v) => setField(field.key, v)}
        />
      ))}
    </Paper>
  );
};

export default ColumnEditor;

const headingStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  color: "#020303",
  mb: 2,
};

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
  mb: 1,
};
